import axios from "axios";

export const backendUrl = process.env.REACT_APP_BACKEND_URL;
export const API = `${backendUrl}/api`;

// El JWT se gestiona exclusivamente con cookie httpOnly (set por el backend).
// No se almacena token en localStorage (mitigación XSS).
export const api = axios.create({ baseURL: API, withCredentials: true });

// FASE 3 — suplantación ("entrar como"): token efímero SOLO en memoria (nunca
// en localStorage). Se adjunta en la cabecera X-Impersonate-Token en cada
// petición mientras esté activo y no caducado.
let _impersonation = null; // { token, email, name, expires }
export function setImpersonation(v) {
  _impersonation = v || null;
  try { window.dispatchEvent(new Event("imp-changed")); } catch (e) { /* noop */ }
}
export function getImpersonation() {
  if (_impersonation && _impersonation.expires && new Date(_impersonation.expires) < new Date()) {
    _impersonation = null;
  }
  return _impersonation;
}
api.interceptors.request.use((cfg) => {
  const i = getImpersonation();
  if (i && i.token) cfg.headers["X-Impersonate-Token"] = i.token;
  return cfg;
});

// Sesión admin corta (10 min). Al recibir 401, intentamos UNA vez refrescar el
// token (cookie httpOnly, refresh de 30 min) y reintentamos la petición. Así las
// páginas no se quedan en "Cargando…" ni vacías por un token caducado.
let _refreshing = null;
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const { response, config } = error;
    if (!response || response.status !== 401 || !config || config._retry) return Promise.reject(error);
    const url = config.url || "";
    if (url.includes("/auth/refresh") || url.includes("/auth/login")) return Promise.reject(error);
    config._retry = true;
    try {
      _refreshing = _refreshing || api.post("/auth/refresh");
      await _refreshing;
      _refreshing = null;
      return api(config);
    } catch (e) {
      _refreshing = null;
      return Promise.reject(error);
    }
  }
);

import { ERR } from "../i18n_errors";

function currentLocale() {
  try { return localStorage.getItem("lura_locale") || "es"; } catch (e) { return "es"; }
}

export function apiError(e, fallback = "Algo se ha torcido. Inténtalo de nuevo.") {
  const d = e?.response?.data?.detail;
  if (d == null) return e?.message || fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => (x?.msg ? x.msg : JSON.stringify(x))).join(" ");
  // Respuesta estructurada del backend: {code, msg}. Traducimos por código al
  // idioma activo; si el código no está localizado, usamos el msg de respaldo.
  if (d?.code) {
    const table = ERR[currentLocale()] || ERR.es;
    if (table[d.code]) return table[d.code];
  }
  if (d?.msg) return d.msg;
  return String(d);
}

// Descarga autenticada (blob) para exportaciones — usa la cookie httpOnly.
export async function downloadFile(path, filename) {
  const imp = getImpersonation();
  const headers = imp && imp.token ? { "X-Impersonate-Token": imp.token } : {};
  const res = await fetch(`${API}${path}`, { credentials: "include", headers });
  if (!res.ok) throw new Error("No se pudo generar el archivo");
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}
